// src/services/insightPrioritizer.ts
//
// Lightweight deduplication + priority ordering for management insight layers.
//
// Priority tiers (highest to lowest):
//   1. Guided interventions  — most actionable, with specific action buttons
//   2. Predictive risks      — forward-looking signals
//   3. Current issues        — attention items about the present state
//   4. General trends        — week-over-week comparisons
//
// When the same underlying issue appears at multiple tiers, the highest-tier
// version is shown and lower-tier duplicates are suppressed.
//
// Deterministic, explainable, no Freedom dependency.

import type { Intervention } from './interventionEngine';

// ── Keyed item ────────────────────────────────────────────────
// Attention items and risk items must carry a `key` for matching.
export interface KeyedInsight {
  key: string;
}

// ── Concept key extraction ────────────────────────────────────
//
// Maps each intervention `id` to the "concept key" it covers.
// Any risk/attention item sharing the same concept key is a duplicate.
//
// Concept key space:
//   goal:{goalId}   — a specific goal's behind / at-risk state
//   48h             — tasks due within 48h not started/blocked
//   overload        — team member overload
//   reopened        — reopened tasks still open
//   blocked         — generic blocked-task signal
//   unestimated     — unestimated tasks
//   overcommit      — too many tasks due this week
//   overdue-trend   — overdue trend (not covered by any intervention, always kept)
//   rebalancing     — pending rebalancing suggestions

export function getInterventionCoverage(interventions: Intervention[]): Set<string> {
  const keys = new Set<string>();
  for (const inv of interventions) {
    if (inv.relatedGoalId)              keys.add(`goal:${inv.relatedGoalId}`);
    if (inv.id.startsWith('focus-48h-')) keys.add('48h');
    if (inv.id.startsWith('reassign-'))  keys.add('overload');
    if (inv.id.startsWith('review-reopen-')) keys.add('reopened');
    if (inv.id.startsWith('unblock-'))   keys.add('blocked');
    if (inv.id === 'estimate-missing')   keys.add('unestimated');
    if (inv.id === 'defer-overcommit')   keys.add('overcommit');
  }
  return keys;
}

// ── Risk-key normalisation ────────────────────────────────────
//
// upcomingRisks uses keys like 'goal-{id}', 'tasks-48h', etc.
// This maps them to the shared concept-key space.
export function normalizeRiskKey(key: string): string {
  if (key.startsWith('goal-'))   return `goal:${key.slice(5)}`;
  if (key === 'tasks-48h')       return '48h';
  if (key === 'team-overload-risk') return 'overload';
  if (key === 'reopened')        return 'reopened';
  return key; // passthrough for keys that already match concept space
}

// ── Suppression ───────────────────────────────────────────────
//
// Removes items whose concept key is already covered by a higher-tier source.
export function suppressCovered<T extends KeyedInsight>(
  items: T[],
  coverage: Set<string>,
): T[] {
  return items.filter((item) => !coverage.has(normalizeRiskKey(item.key)));
}
